import { useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { PreChatIntentModal, type PreChatIntentCatalog } from '../PreChatIntentModal';

type FirstContactIntentFallbackProps = {
  /** 'fa' (086 first-contact intent, default) or 'sp' (102 compose-time subject). */
  catalog?: PreChatIntentCatalog;
  providerName: string;
  /** Present only for SP-targeted conversations — an SP contacting an FA has none. */
  serviceProviderId?: string;
  activeWorkspaceOwnerId: string;
  recipientUserId: string;
  /**
   * Called after Continue writes the record. `usePendingFirstMessageIntent`
   * re-reads sessionStorage on every render but has no subscription to it —
   * the modal's own local state closing doesn't re-render `ChatInterface`,
   * so the parent must be told explicitly to re-render and pick it up.
   */
  onIntentResolved: () => void;
};

/**
 * Defensive fallback (spec §3.5, ticket 08) for the rare case a first send to
 * an FA→SP pair has no pending-intent record — the launch gate should always
 * have produced one, but an eligibility-read failure or an expired/discarded
 * record can still leave one missing. Reopens the same pre-chat modal rather
 * than letting an unclassified message send.
 */
export const FirstContactIntentFallback = ({
  catalog = 'fa',
  providerName,
  serviceProviderId,
  activeWorkspaceOwnerId,
  recipientUserId,
  onIntentResolved,
}: FirstContactIntentFallbackProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div role="alert" className="border-t bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="mb-2">
        Choose what this conversation is about before sending your first message.
      </p>
      <Button size="sm" variant="outline" onClick={() => setModalOpen(true)}>
        Choose reason
      </Button>
      <PreChatIntentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        catalog={catalog}
        providerName={providerName}
        activeWorkspaceOwnerId={activeWorkspaceOwnerId}
        serviceProviderId={serviceProviderId}
        recipientUserId={recipientUserId}
        sourceSurface="chat_fallback"
        onContinue={() => {
          setModalOpen(false);
          onIntentResolved();
        }}
      />
    </div>
  );
};
